import React, { useMemo } from 'react';
import { Table } from 'antd';
import './ndoeUi.css';
import { useIsDesktop } from './useMediaQuery';
import NdoeMobileList from './NdoeMobileList';
import { NdoeEmpty, NdoeLoading, NdoeError } from './NdoeStates';
import { carteDepuisColonnes } from './carteDepuisColonnes';
import PagerMobile from './PagerMobile';

/**
 * NdoeResponsiveTable — LE pattern liste du design system.
 *  - Desktop (>=768px) : Table AntD stylée (.ndoe-table), paginée.
 *  - Mobile  (<768px)  : liste de cartes, JAMAIS de table compressée.
 *
 * La logique de données (fetch, pagination serveur, filtres) reste dans la page :
 * ce composant ne fait que CHOISIR la présentation selon la largeur.
 *
 * `renderCard` est FACULTATIF depuis le lot 3 : sans lui, la carte se déduit
 * des `columns` (voir carteDepuisColonnes). C'est ce qui abaisse le coût de
 * migration des 75 fichiers qui rendent encore un <Table> antd brut, donc sans
 * aucun rendu mobile (comptage sur origin/main au 16/07/2026 ; 38 fichiers
 * utilisent déjà ce composant). Une carte sur mesure reste possible, et
 * meilleure, quand l'écran le mérite.
 *
 * CE QUE LA VUE MOBILE NE FAIT PAS : ni tri, ni filtres de colonne (il n'y a
 * plus d'en-tête). Une page dont le filtrage passe par `filters`/`onFilter`
 * antd perd ce filtre sur téléphone — prévoir un NdoeFilterBar si le filtre
 * compte.
 *
 * Props :
 *  - data        : tableau d'items (page courante)
 *  - columns     : colonnes AntD (desktop)
 *  - renderCard  : (item, index) => node pour le mobile (FACULTATIF — déduit
 *                  des colonnes si absent)
 *  - onCardClick   : (item) => void — rend la carte auto cliquable (drill-down)
 *  - cardTitreCol  : `key` de la colonne à prendre comme titre de la carte auto
 *                    (elle est alors consommée : pas de doublon)
 *  - cardTitre     : (item) => node — titre libre. N'utiliser que si aucune
 *                    colonne ne convient : les colonnes restent TOUTES en
 *                    métriques, donc un titre qui recopie une colonne
 *                    s'afficherait deux fois. Préférer cardTitreCol.
 *  - rowKey      : clé (string dataIndex ou fonction) — défaut 'id'
 *  - loading     : bool
 *  - error       : bool | string
 *  - onRetry     : callback état erreur
 *  - pagination  : objet pagination AntD (desktop) ; réutilisé pour le mobile
 *  - onChange    : handler AntD Table onChange (tri/pagination serveur)
 *  - empty       : { icon, title, text, action }
 *  - mobilePager : node de pagination custom pour le mobile (optionnel — déduit
 *                  de `pagination` si absent, voir PagerMobile)
 *  - onRow       : (record, index) => props de ligne AntD (ex. drill-down au clic)
 *  - size        : densité de la table DESKTOP ('small' | 'middle' | 'large').
 *                  Laissé vide, antd garde sa densité par défaut — donc les
 *                  écrans déjà migrés ne bougent pas. Utile aux pages qui
 *                  rendaient un <Table size="small"> avant migration : sans
 *                  ce relais, migrer vers les cartes mobiles ÉLARGISSAIT au
 *                  passage les lignes sur ordinateur, sans que personne ne l'ait
 *                  demandé.
 *  - className
 */
export default function NdoeResponsiveTable({
  data = [],
  columns = [],
  renderCard,
  onCardClick,
  cardTitre,
  cardTitreCol,
  rowKey = 'id',
  loading = false,
  error = false,
  onRetry,
  pagination,
  onChange,
  empty = {},
  scroll,
  mobilePager = null,
  onRow,
  size,
  className = '',
  stickyHeader = true,
}) {
  const isDesktop = useIsDesktop();

  // Carte déduite des colonnes quand la page n'en fournit pas.
  // Le useMemo ne sert QUE si la page mémoïse elle-même ses `columns` ; la
  // plupart les reconstruisent dans leur corps de composant, et la dépendance
  // change alors à chaque rendu. Sans conséquence (ni la liste ni les cartes ne
  // sont mémoïsées, elles se re-rendraient de toute façon) — mais ne comptez
  // pas dessus pour une optimisation que ce code ne fournit pas.
  const carteAuto = useMemo(
    () => carteDepuisColonnes(columns, {
      onClick: onCardClick,
      titreDe: cardTitre,
      titreCol: cardTitreCol,
    }),
    [columns, onCardClick, cardTitre, cardTitreCol],
  );
  const carte = renderCard || carteAuto;

  // LARGEUR PLANCHER DES COLONNES (31/07/2026).
  //
  // Signalé sur /reception : dans la modale, « TYPE DE MOUVEMENT » s'affichait
  // « TYPE DE MOUVEME NT », la date « 25-07-20: » et le bouton « Annuler »
  // était coupé net. Mesuré : 11 colonnes à EXACTEMENT 101 px chacune.
  //
  // Pourquoi. Une Table antd avec `scroll.x` passe en `table-layout: fixed`.
  // Les colonnes sans `width` déclarée se partagent alors l'espace À PARTS
  // ÉGALES. `scroll={{ x: 400 }}` — la valeur recopiée dans une soixantaine
  // d'écrans — ne dit pas « au moins 400 px » de façon utile : dès que le
  // conteneur est plus large, la table s'y étale et divise. Onze colonnes dans
  // 1 100 px font 100 px chacune, quoi que contienne la cellule.
  //
  // Le contenu ne rétrécit pas pour autant : il est ROGNÉ. Un tableau illisible
  // ne signale rien — il a l'air normal, simplement faux.
  //
  // On impose donc un plancher : la table ne descend jamais sous
  // `colonnes × MIN_COL`. Au-delà, elle DÉFILE horizontalement dans son
  // conteneur — ce qui est la bonne issue, et celle que `scroll.x` était censé
  // produire. Un `scroll.x` plus généreux fourni par l'appelant est respecté.
  // 140 px : mesuré sur le contenu réel le plus large qui revient partout — une
  // étiquette « date & heure » avec son icône (« 03-07-2026 16:47 ») demande
  // ~162 px, un nom de cliente ~142 px. On ne monte pas jusqu'à 162 : sur une
  // table à 12 colonnes cela imposerait 1 950 px, donc un défilement permanent
  // même sur grand écran. Le reste est rattrapé par le filet ci-dessous
  // (.ndoe-table .ant-tag, ndoeUi.css) : ce qui dépasse encore se termine par
  // une ellipsis et porte son texte complet en infobulle — tronqué VISIBLEMENT,
  // jamais coupé en plein caractère comme « 25-07-20: ».
  const MIN_COL = 140;
  const scrollEffectif = useMemo(() => {
    const plancher = (columns?.length || 0) * MIN_COL;
    if (!plancher) return scroll;
    const xFourni = Number(scroll?.x);
    return { ...scroll, x: Math.max(plancher, Number.isFinite(xFourni) ? xFourni : 0) };
  }, [columns, scroll]);

  if (isDesktop) {
    return (
      <div className={`ndoe-table ${className}`}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey={rowKey}
          loading={loading}
          pagination={pagination}
          onChange={onChange}
          onRow={onRow}
          size={size}
          scroll={scrollEffectif}
          /* ⑩ En-tête collant : le thead reste visible sous la topbar (60px)
             quand on fait défiler une longue liste. */
          sticky={stickyHeader ? { offsetHeader: 60 } : undefined}
          locale={{
            emptyText: (
              <NdoeEmpty icon={empty.icon} title={empty.title} text={empty.text} action={empty.action} />
            ),
          }}
        />
      </div>
    );
  }

  // --- Mobile ---
  if (loading) return <NdoeLoading rows={empty.rows || 4} />;
  if (error) {
    return <NdoeError text={typeof error === 'string' ? error : undefined} onRetry={onRetry} />;
  }

  const rowKeyFn =
    typeof rowKey === 'function' ? rowKey : (item, i) => item?.[rowKey] ?? i;

  return (
    <>
      <NdoeMobileList data={data} renderCard={carte} rowKey={rowKeyFn} empty={empty} />
      {/* Un pager sur mesure gagne ; sinon on déduit de `pagination`, faute de
          quoi la liste mobile s'arrêterait à la page 1 sans le dire. */}
      {mobilePager || <PagerMobile pagination={pagination} onChange={onChange} />}
    </>
  );
}
